package features.booking

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.lazy.LazyColumn
import org.example.abigail.imprimirBoleto
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.LocationOn
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.DirectionsBus
import androidx.compose.material.icons.filled.KeyboardReturn
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.foundation.Image
import org.jetbrains.compose.resources.painterResource
import abi.composeapp.generated.resources.Res
import abi.composeapp.generated.resources.logotipo_smartvan
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Timer
import androidx.compose.material.icons.filled.Phone
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Shield
import androidx.compose.material.icons.filled.ConfirmationNumber
import androidx.compose.material.icons.filled.QrCode2
import androidx.compose.material.icons.filled.AcUnit
import androidx.compose.material.icons.filled.Luggage
import androidx.compose.material.icons.filled.FileDownload
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Gavel
import androidx.compose.material.icons.filled.PrivacyTip
import androidx.compose.material.icons.filled.TripOrigin
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.Receipt

/** Convierte "HH:MM AM/PM" a minutos desde medianoche, para ordenar horarios (más temprano primero). */
internal fun horaAMinutos(h: String): Int {
    return try {
        val esPm = h.contains("PM", ignoreCase = true)
        val esAm = h.contains("AM", ignoreCase = true)
        val limpio = h.replace("AM", "", ignoreCase = true).replace("PM", "", ignoreCase = true).trim()
        val partes = limpio.split(":")
        var hh = partes[0].trim().toInt()
        val mm = partes.getOrNull(1)?.trim()?.toIntOrNull() ?: 0
        if (esPm && hh != 12) hh += 12
        if (esAm && hh == 12) hh = 0
        hh * 60 + mm
    } catch (e: Exception) { 0 }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BookingScreen(
    viewModel: BookingViewModel,
    isAdminLoggedIn: Boolean,
    onBackClick: () -> Unit,
    onBoletoComprado: () -> Unit,
    onOpenLoginClick: () -> Unit,
    onOpenTracking: () -> Unit = {}
) {
    val currentStep by viewModel.currentStep.collectAsState()
    val seats by viewModel.seats.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val pasajerosMap by viewModel.pasajeros.collectAsState()
    val precios by viewModel.precios.collectAsState()
    val precioBase = precios["base"] ?: 350
    val precioMaleta = precios["maleta"] ?: 150
    val precioMascota = precios["mascota"] ?: 150
    val selectedSeatsCount = seats.count { it.status == SeatStatus.SELECTED }
    val esIdaYVueltaGlobal by viewModel.esIdaYVuelta.collectAsState()
    val multiplicador = if (esIdaYVueltaGlobal) 2 else 1
    val totalToPay = if (pasajerosMap.isNotEmpty()) {
        pasajerosMap.values.sumOf { p -> precioBase + (p.maletasExtras * precioMaleta) + (if (p.llevaMascota) precioMascota else 0) } * multiplicador
    } else {
        selectedSeatsCount * precioBase * multiplicador
    }
    val uriHandler = LocalUriHandler.current

    Scaffold(
        floatingActionButton = {
            FloatingActionButton(
                onClick = {
                    val selectedSeats = seats.filter { it.status == SeatStatus.SELECTED }
                    val message = if (selectedSeats.isEmpty()) {
                        "Hola SMARTVAN, necesito ayuda con una reserva."
                    } else {
                        val seatsText = selectedSeats.joinToString { it.id.toString() }
                        "Hola SMARTVAN, quiero confirmar la reserva de los asientos: $seatsText. Mi total es de $$totalToPay para el viaje del ${viewModel.fecha.value} a las ${viewModel.horario.value}."
                    }
                    val encodedMessage = message.replace(" ", "%20")
                    val phoneNumber = viewModel.whatsappSoporte.value.ifBlank { "525573051786" }
                    uriHandler.openUri("https://wa.me/$phoneNumber?text=$encodedMessage")
                },
                containerColor = Color(0xFF25D366),
                contentColor = Color.White,
                shape = CircleShape
            ) {
                Icon(Icons.Filled.Phone, contentDescription = "WhatsApp", tint = Color.White, modifier = Modifier.size(26.dp))
            }
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(SmartVanColors.FondoPanel)
                .padding(paddingValues),
            contentAlignment = Alignment.TopCenter
        ) {
            val mostrarTemporizador by viewModel.mostrarTemporizador.collectAsState()
            val tiempoRestanteMilis by viewModel.tiempoRestanteMilis.collectAsState()

            if (currentStep == BookingStep.BUSCAR_VIAJE) {
                // Buscador ocupa toda la pantalla sin padding
                BuscadorViajesView(
                    viewModel = viewModel,
                    isAdminLoggedIn = isAdminLoggedIn,
                    onOpenLoginClick = onOpenLoginClick,
                    onOpenTracking = onOpenTracking
                )
            } else {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .widthIn(max = 650.dp)
                        .padding(16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // Progress Stepper
                    val stepActual = when (currentStep) {
                        BookingStep.ELEGIR_ASIENTOS -> 1
                        BookingStep.LLENAR_MANIFIESTO -> 2
                        BookingStep.RESUMEN_COMPRA -> 3
                        BookingStep.VER_BOLETOS -> 4
                        else -> 0
                    }
                    if (currentStep != BookingStep.VER_BOLETOS) {
                        ProgressStepper(currentStep = stepActual, totalSteps = 3)
                        Spacer(modifier = Modifier.height(12.dp))
                    }

                    if (mostrarTemporizador) {
                        val minutos = (tiempoRestanteMilis / 1000) / 60
                        val segundos = (tiempoRestanteMilis / 1000) % 60
                        Card(
                            colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo),
                            modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Filled.Timer, contentDescription = null, tint = Color.Red)
                                Spacer(Modifier.width(8.dp))
                                Text("Tienes ${minutos.toString().padStart(2, '0')}:${segundos.toString().padStart(2, '0')} para completar tu pago", color = Color.Red, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyMedium)
                            }
                        }
                    }

                    when (currentStep) {
                        BookingStep.ELEGIR_ASIENTOS -> SeleccionAsientosView(viewModel, seats, isLoading, selectedSeatsCount, totalToPay)
                        BookingStep.LLENAR_MANIFIESTO -> ManifiestoPasajerosView(viewModel)
                        BookingStep.RESUMEN_COMPRA -> ResumenCompraView(viewModel = viewModel, totalToPay = totalToPay, onIrAPagar = onBoletoComprado)
                        BookingStep.VER_BOLETOS -> BoletosDigitalesView(viewModel = viewModel)
                        else -> {}
                    }
                }
            }
        }
    }
}

@Composable
fun ProgressStepper(currentStep: Int, totalSteps: Int) {
    val labels = listOf("Asientos", "Pasajeros", "Pago")
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        for (i in 1..totalSteps) {
            val isCompleted = i < currentStep
            val isCurrent = i == currentStep
            val color = when {
                isCompleted -> SmartVanColors.Verde
                isCurrent -> SmartVanColors.CyanAccent
                else -> Color.LightGray
            }

            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.weight(1f)
            ) {
                Box(
                    modifier = Modifier.size(32.dp).background(color, CircleShape),
                    contentAlignment = Alignment.Center
                ) {
                    if (isCompleted) {
                        // Checkmark
                        Canvas(modifier = Modifier.size(14.dp)) {
                            val path = Path().apply {
                                moveTo(size.width * 0.15f, size.height * 0.5f)
                                lineTo(size.width * 0.4f, size.height * 0.8f)
                                lineTo(size.width * 0.85f, size.height * 0.2f)
                            }
                            drawPath(path, Color.White, style = Stroke(width = 2.dp.toPx(), cap = StrokeCap.Round, join = StrokeJoin.Round))
                        }
                    } else {
                        Text("$i", color = Color.White, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                    }
                }
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    labels.getOrElse(i - 1) { "" },
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = if (isCurrent) FontWeight.Bold else FontWeight.Normal,
                    color = if (isCurrent) SmartVanColors.CyanAccent else Color.Gray
                )
            }

            if (i < totalSteps) {
                Box(
                    modifier = Modifier
                        .weight(0.5f)
                        .height(2.dp)
                        .background(if (i < currentStep) SmartVanColors.Verde else Color.LightGray)
                )
            }
        }
    }
}

@Composable
fun BuscadorViajesView(viewModel: BookingViewModel, isAdminLoggedIn: Boolean = false, onOpenLoginClick: () -> Unit = {}, onOpenTracking: () -> Unit = {}) {
    val origen by viewModel.origen.collectAsState()
    val destino by viewModel.destino.collectAsState()
    val fecha by viewModel.fecha.collectAsState()
    val horario by viewModel.horario.collectAsState()
    val horariosDisponibles by viewModel.horariosDisponibles.collectAsState()
    val esIdaYVuelta by viewModel.esIdaYVuelta.collectAsState()
    val fechaVuelta by viewModel.fechaVuelta.collectAsState()
    val horarioVuelta by viewModel.horarioVuelta.collectAsState()
    val horariosVueltaDisponibles by viewModel.horariosVueltaDisponibles.collectAsState()

    val ciudadesDisponibles by viewModel.ciudades.collectAsState()

    Box(
        modifier = Modifier.fillMaxSize().background(Color(0xFFF4F6F8)),
        contentAlignment = Alignment.TopCenter
    ) {
        // Hero gradient background - full width
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(380.dp)
                .background(
                    brush = androidx.compose.ui.graphics.Brush.verticalGradient(
                        colors = listOf(Color(0xFF001033), SmartVanColors.AzulOscuro, Color(0xFF003380))
                    )
                )
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Top buttons row - full width over gradient
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                TextButton(onClick = { onOpenTracking() }) {
                    Icon(Icons.Filled.LocationOn, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(18.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text("Rastrear Viaje", color = SmartVanColors.CyanAccent, fontWeight = FontWeight.Bold)
                }
                TextButton(onClick = { onOpenLoginClick() }) {
                    Text(
                        text = if (isAdminLoggedIn) "Panel Admin" else "Iniciar Sesion",
                        color = Color.White.copy(alpha = 0.9f),
                        fontWeight = FontWeight.Bold
                    )
                }
            }

            // Header content
            Column(
                modifier = Modifier
                    .widthIn(max = 650.dp)
                    .padding(horizontal = 20.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {

                Box(
                    modifier = Modifier
                        .background(Color.White, shape = CircleShape)
                        .padding(14.dp)
                ) {
                    Image(
                        painter = painterResource(Res.drawable.logotipo_smartvan),
                        contentDescription = "Logotipo SMARTVAN",
                        modifier = Modifier.size(height = 90.dp, width = 150.dp),
                        colorFilter = null
                    )
                }

                Spacer(modifier = Modifier.height(16.dp))

                Text(
                    text = "Viaja cómodo, viaja seguro",
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Black,
                    color = Color.White
                )
                Text(
                    text = "Reserva tu lugar en segundos",
                    style = MaterialTheme.typography.bodyLarge,
                    color = Color.White.copy(alpha = 0.7f),
                    modifier = Modifier.padding(top = 4.dp, bottom = 28.dp)
                )

                // Search Card
                Card(
                    colors = CardDefaults.cardColors(containerColor = Color.White),
                    shape = RoundedCornerShape(20.dp),
                    elevation = CardDefaults.cardElevation(defaultElevation = 12.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(
                        modifier = Modifier.padding(20.dp),
                        verticalArrangement = Arrangement.spacedBy(14.dp)
                    ) {
                        SelectorUbicacion(
                            label = "Origen",
                            value = origen,
                            options = ciudadesDisponibles,
                            onValueChange = {
                                viewModel.setOrigen(it)
                                viewModel.actualizarBusqueda()
                            }
                        )

                        SelectorUbicacion(
                            label = "Destino",
                            value = destino,
                            options = ciudadesDisponibles,
                            onValueChange = {
                                viewModel.setDestino(it)
                                viewModel.actualizarBusqueda()
                            }
                        )

                        DateCarousel(
                            selectedDate = fecha,
                            onDateSelected = { nuevaFecha -> viewModel.seleccionarFecha(nuevaFecha) }
                        )

                        if (fecha.isNotBlank() && origen.isNotBlank() && destino.isNotBlank()) {
                            if (horariosDisponibles.isEmpty()) {
                                Card(
                                    colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo),
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(12.dp)
                                ) {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        modifier = Modifier.padding(12.dp)
                                    ) {
                                        Icon(
                                            Icons.Filled.Warning,
                                            contentDescription = "Alerta",
                                            tint = Color(0xFFD32F2F),
                                            modifier = Modifier.padding(end = 8.dp)
                                        )
                                        Text(
                                            "No hay viajes programados para esta ruta en este día.",
                                            color = Color(0xFFD32F2F),
                                            style = MaterialTheme.typography.bodySmall,
                                            fontWeight = FontWeight.Bold
                                        )
                                    }
                                }
                            } else {
                                // Ordenar de más temprano a más tarde
                                val horariosOrdenados = horariosDisponibles.sortedBy { horaAMinutos(it) }
                                // Auto-seleccionar el más temprano cuando solo hay un viaje
                                LaunchedEffect(horariosDisponibles) {
                                    if (horariosOrdenados.size == 1 && horario != horariosOrdenados.first()) {
                                        viewModel.setHorario(horariosOrdenados.first())
                                    }
                                }
                                Text(
                                    if (horariosOrdenados.size > 1) "Elige tu salida" else "Salida del día",
                                    fontWeight = FontWeight.Bold, color = Color.DarkGray
                                )
                                Spacer(modifier = Modifier.height(4.dp))
                                CarruselHorizontal(
                                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    items(horariosOrdenados) { hora ->
                                        val isSelected = horario == hora
                                        Card(
                                            colors = CardDefaults.cardColors(
                                                containerColor = if (isSelected) SmartVanColors.AzulOscuro else Color(0xFFF3F4F6)
                                            ),
                                            modifier = Modifier.clickable { viewModel.setHorario(hora) },
                                            shape = RoundedCornerShape(12.dp),
                                            border = if (isSelected) null else BorderStroke(1.dp, Color.LightGray)
                                        ) {
                                            Column(
                                                modifier = Modifier.padding(horizontal = 20.dp, vertical = 10.dp),
                                                horizontalAlignment = Alignment.CenterHorizontally
                                            ) {
                                                Text(
                                                    "Salida",
                                                    color = if (isSelected) Color.White.copy(alpha = 0.8f) else Color.Gray,
                                                    fontSize = 10.sp, fontWeight = FontWeight.Medium
                                                )
                                                Text(
                                                    hora,
                                                    color = if (isSelected) Color.White else Color.DarkGray,
                                                    fontWeight = FontWeight.Bold,
                                                    style = MaterialTheme.typography.titleMedium
                                                )
                                            }
                                        }
                                    }
                                }
                                Spacer(modifier = Modifier.height(6.dp))
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(16.dp))
                                    Spacer(modifier = Modifier.width(6.dp))
                                    Text(
                                        if (horariosOrdenados.size == 1) "Viaje disponible · verás los puntos y horarios de abordaje al elegir asientos"
                                        else "${horariosOrdenados.size} salidas disponibles · elige una",
                                        color = SmartVanColors.VerdeOscuro, fontSize = 12.sp, fontWeight = FontWeight.Medium
                                    )
                                }
                            }
                        }

                        // ═══ Toggle Ida / Ida y Vuelta ═══
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.Center,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            val opcionIda = !esIdaYVuelta
                            FilterChip(
                                selected = opcionIda,
                                onClick = { viewModel.toggleIdaYVuelta(false) },
                                label = { Text("Solo Ida", fontWeight = if (opcionIda) FontWeight.Bold else FontWeight.Normal) },
                                leadingIcon = if (opcionIda) { { Icon(Icons.Filled.CheckCircle, contentDescription = null, modifier = Modifier.size(16.dp)) } } else null,
                                colors = FilterChipDefaults.filterChipColors(
                                    selectedContainerColor = SmartVanColors.CyanAccent.copy(alpha = 0.15f),
                                    selectedLabelColor = SmartVanColors.AzulOscuro
                                )
                            )
                            Spacer(modifier = Modifier.width(12.dp))
                            FilterChip(
                                selected = esIdaYVuelta,
                                onClick = { viewModel.toggleIdaYVuelta(true) },
                                label = { Text("Ida y Vuelta", fontWeight = if (esIdaYVuelta) FontWeight.Bold else FontWeight.Normal) },
                                leadingIcon = if (esIdaYVuelta) { { Icon(Icons.Filled.CheckCircle, contentDescription = null, modifier = Modifier.size(16.dp)) } } else null,
                                colors = FilterChipDefaults.filterChipColors(
                                    selectedContainerColor = SmartVanColors.Verde.copy(alpha = 0.15f),
                                    selectedLabelColor = SmartVanColors.AzulOscuro
                                )
                            )
                        }

                        // ═══ Campos de vuelta (solo si es ida y vuelta) ═══
                        if (esIdaYVuelta) {
                            Card(
                                colors = CardDefaults.cardColors(containerColor = SmartVanColors.Verde.copy(alpha = 0.05f)),
                                shape = RoundedCornerShape(14.dp),
                                border = BorderStroke(1.dp, SmartVanColors.Verde.copy(alpha = 0.3f)),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Column(
                                    modifier = Modifier.padding(14.dp),
                                    verticalArrangement = Arrangement.spacedBy(10.dp)
                                ) {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Filled.TripOrigin, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(18.dp))
                                        Spacer(modifier = Modifier.width(8.dp))
                                        Text("Viaje de Regreso", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro)
                                        Spacer(modifier = Modifier.width(6.dp))
                                        Text("${destino} → ${origen}", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                                    }

                                    DateCarousel(
                                        selectedDate = fechaVuelta,
                                        onDateSelected = { nuevaFecha -> viewModel.seleccionarFechaVuelta(nuevaFecha) }
                                    )

                                    if (fechaVuelta.isNotBlank() && horariosVueltaDisponibles.isEmpty()) {
                                        Text(
                                            "No hay viajes de vuelta para esta fecha.",
                                            color = Color(0xFFD32F2F),
                                            style = MaterialTheme.typography.bodySmall,
                                            fontWeight = FontWeight.Bold
                                        )
                                    } else if (horariosVueltaDisponibles.isNotEmpty()) {
                                        Text("Horario de Regreso", fontWeight = FontWeight.Bold, color = Color.DarkGray, style = MaterialTheme.typography.bodySmall)
                                        CarruselHorizontal(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                                            items(horariosVueltaDisponibles.sortedBy { horaAMinutos(it) }) { hora ->
                                                val isSelected = horarioVuelta == hora
                                                Card(
                                                    colors = CardDefaults.cardColors(
                                                        containerColor = if (isSelected) SmartVanColors.Verde else Color(0xFFF3F4F6)
                                                    ),
                                                    modifier = Modifier.clickable { viewModel.setHorarioVuelta(hora) },
                                                    shape = RoundedCornerShape(10.dp),
                                                    border = if (isSelected) null else BorderStroke(1.dp, Color.LightGray)
                                                ) {
                                                    Text(
                                                        text = hora,
                                                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                                                        color = if (isSelected) Color.White else Color.DarkGray,
                                                        fontWeight = FontWeight.Bold,
                                                        style = MaterialTheme.typography.bodyMedium
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        val vueltaCompleta = !esIdaYVuelta || (fechaVuelta.isNotBlank() && horarioVuelta.isNotBlank())
                        val isFormularioCompleto = origen.isNotBlank() && destino.isNotBlank() && fecha.isNotBlank() && horariosDisponibles.isNotEmpty() && horario.isNotBlank() && vueltaCompleta

                        Button(
                            onClick = { if (isFormularioCompleto) viewModel.avanzarAAsientos() },
                            modifier = Modifier.fillMaxWidth().height(54.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = if (isFormularioCompleto) SmartVanColors.CyanAccent else Color.LightGray
                            ),
                            enabled = isFormularioCompleto,
                            shape = RoundedCornerShape(14.dp)
                        ) {
                            Icon(Icons.Filled.Search, contentDescription = null, modifier = Modifier.size(20.dp))
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                if (esIdaYVuelta) "Buscar Asientos (Ida y Vuelta)" else "Buscar Asientos Disponibles",
                                fontWeight = FontWeight.Bold, color = Color.White
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(40.dp))

                // Benefits Section
                Text(
                    text = "Viaja con las mejores comodidades",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Black,
                    color = SmartVanColors.AzulOscuro,
                    modifier = Modifier.padding(bottom = 20.dp)
                )

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    BenefitCard(modifier = Modifier.weight(1f), icon = Icons.Filled.DirectionsBus, title = "Unidades Nuevas", subtitle = "Flota moderna y segura")
                    BenefitCard(modifier = Modifier.weight(1f), icon = Icons.Filled.Timer, title = "Puntualidad", subtitle = "Horarios exactos")
                    BenefitCard(modifier = Modifier.weight(1f), icon = Icons.Filled.Phone, title = "Atención WhatsApp", subtitle = "Soporte directo al instante")
                }

                Spacer(modifier = Modifier.height(12.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    BenefitCard(modifier = Modifier.weight(1f), icon = Icons.Filled.DateRange, title = "Reserva Fácil", subtitle = "Compra en 2 minutos")
                    BenefitCard(modifier = Modifier.weight(1f), icon = Icons.Filled.ConfirmationNumber, title = "Boleto Digital", subtitle = "Sin imprimir, desde tu celular")
                    BenefitCard(modifier = Modifier.weight(1f), icon = Icons.Filled.Phone, title = "Atención Directa", subtitle = "Te apoyamos por WhatsApp")
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Aviso de precios
                Card(
                    colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Filled.Info, contentDescription = null, tint = Color(0xFFF9A825), modifier = Modifier.size(18.dp))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            "Los precios están sujetos a cambio sin previo aviso.",
                            style = MaterialTheme.typography.bodySmall,
                            color = Color(0xFF6D4C00)
                        )
                    }
                }

                Spacer(modifier = Modifier.height(40.dp))

                // Footer
                HorizontalDivider(color = Color.LightGray.copy(alpha = 0.5f))
                Spacer(modifier = Modifier.height(20.dp))

                var mostrarTerminos by remember { mutableStateOf(false) }
                var mostrarPrivacidad by remember { mutableStateOf(false) }

                Text(
                    "SmartVan - Viaja cómodo, viaja seguro.",
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Bold,
                    color = SmartVanColors.AzulOscuro
                )
                Text(
                    "smartvanmx.com",
                    style = MaterialTheme.typography.bodySmall,
                    color = SmartVanColors.CyanAccent,
                    modifier = Modifier.padding(top = 4.dp)
                )

                Row(
                    modifier = Modifier.padding(top = 8.dp),
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Text(
                        "Términos y Condiciones",
                        style = MaterialTheme.typography.bodySmall,
                        color = SmartVanColors.CyanAccent,
                        fontWeight = FontWeight.Medium,
                        modifier = Modifier.clickable { mostrarTerminos = true }
                    )
                    Text(
                        "Aviso de Privacidad",
                        style = MaterialTheme.typography.bodySmall,
                        color = SmartVanColors.CyanAccent,
                        fontWeight = FontWeight.Medium,
                        modifier = Modifier.clickable { mostrarPrivacidad = true }
                    )
                }

                val anioActual = remember { org.example.abigail.obtenerFechaHoy().take(4) }
                Text(
                    "© $anioActual SmartVan. Todos los derechos reservados.",
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.Gray,
                    modifier = Modifier.padding(top = 6.dp, bottom = 24.dp)
                )

                if (mostrarTerminos) {
                    TerminosYCondicionesDialog(onDismiss = { mostrarTerminos = false })
                }
                if (mostrarPrivacidad) {
                    AvisoDePrivacidadDialog(onDismiss = { mostrarPrivacidad = false })
                }
            }
        }
    }
}

@Composable
fun BenefitCard(modifier: Modifier = Modifier, icon: androidx.compose.ui.graphics.vector.ImageVector, title: String, subtitle: String) {
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        shape = RoundedCornerShape(16.dp)
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .background(SmartVanColors.CyanAccent.copy(alpha = 0.1f), CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(icon, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(24.dp))
            }
            Spacer(modifier = Modifier.height(10.dp))
            Text(title, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodyMedium)
            Text(subtitle, color = Color.Gray, style = MaterialTheme.typography.bodySmall, maxLines = 2)
        }
    }
}
@Composable
fun SeleccionAsientosView(viewModel: BookingViewModel, seats: List<Seat>, isLoading: Boolean, selectedSeatsCount: Int, totalToPay: Int) {

    val sesionExpirada by viewModel.sesionExpiradaPorAbandono.collectAsState()
    val mostrarTemporizador by viewModel.mostrarTemporizador.collectAsState()
    val tiempoRestanteMilis by viewModel.tiempoRestanteMilis.collectAsState()
    val mostrarAviso10Minutos by viewModel.mostrarAviso10Minutos.collectAsState()
    val errorAsientosGanados by viewModel.errorAsientosGanados.collectAsState()
    val origen by viewModel.origen.collectAsState()
    val destino by viewModel.destino.collectAsState()
    val fecha by viewModel.fecha.collectAsState()
    val horario by viewModel.horario.collectAsState()
    val puntosSubidaViaje by viewModel.puntosSubidaViaje.collectAsState()
    val puntoSubidaActual by viewModel.puntoSubida.collectAsState()
    val puntosSubidaInfoAll by viewModel.puntosSubidaInfo.collectAsState()
    // Tramo actual (ida/vuelta): la escucha en vivo y el viaje mostrado dependen del tramo
    val tramo by viewModel.tramoActual.collectAsState()
    val esIdaYVuelta by viewModel.esIdaYVuelta.collectAsState()
    val fechaVuelta by viewModel.fechaVuelta.collectAsState()
    val horarioVuelta by viewModel.horarioVuelta.collectAsState()
    val rutaTramoUi = if (tramo == Tramo.VUELTA) "$destino - $origen" else "$origen - $destino"
    val fechaTramoUi = if (tramo == Tramo.VUELTA) fechaVuelta else fecha
    val horarioTramoUi = if (tramo == Tramo.VUELTA) horarioVuelta else horario

    LaunchedEffect(rutaTramoUi, fechaTramoUi, horarioTramoUi) {
        if (rutaTramoUi.isNotBlank() && fechaTramoUi.isNotBlank() && horarioTramoUi.isNotBlank()) {
            viewModel.escucharAsientosEnVivo(rutaTramoUi, fechaTramoUi, horarioTramoUi)
                .collect { ocupados ->
                    viewModel.actualizarAsientosEnVivo(ocupados)
                }
        }
    }
    Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.verticalScroll(rememberScrollState())) {
        // Banner del tramo (solo en ida y vuelta)
        if (esIdaYVuelta) {
            val esVuelta = tramo == Tramo.VUELTA
            Card(
                colors = CardDefaults.cardColors(containerColor = if (esVuelta) SmartVanColors.NaranjaDeep.copy(alpha = 0.15f) else SmartVanColors.Verde.copy(alpha = 0.15f)),
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth().padding(bottom = 10.dp)
            ) {
                Row(modifier = Modifier.fillMaxWidth().padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Icon(if (esVuelta) Icons.Filled.KeyboardReturn else Icons.Filled.DirectionsBus, contentDescription = null, tint = if (esVuelta) SmartVanColors.NaranjaDeep else SmartVanColors.VerdeOscuro, modifier = Modifier.size(18.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        if (esVuelta) "Paso 2 de 2 · Viaje de VUELTA" else "Paso 1 de 2 · Viaje de IDA",
                        fontWeight = FontWeight.Black, color = if (esVuelta) SmartVanColors.NaranjaDeep else SmartVanColors.VerdeOscuro, style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
        }
        // Trip context card
        Card(
            colors = CardDefaults.cardColors(containerColor = SmartVanColors.AzulOscuro),
            shape = RoundedCornerShape(14.dp),
            modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(14.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(20.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Column {
                        Text(rutaTramoUi, color = Color.White, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyMedium)
                        val horaSubida = puntosSubidaInfoAll.find { it.nombre == puntoSubidaActual }?.hora ?: ""
                        Text("$fechaTramoUi${if (horaSubida.isNotBlank()) " | $horaSubida" else ""}", color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall)
                    }
                }
                TextButton(onClick = { viewModel.retrocederABuscador() }) {
                    Text("Cambiar", color = SmartVanColors.CyanAccent, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                }
            }
        }

        // Selector de punto de subida del cliente
        if (puntosSubidaViaje.size > 1) {
            Card(
                colors = CardDefaults.cardColors(containerColor = Color(0xFFE3F2FD)),
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp)
            ) {
                Column(modifier = Modifier.padding(14.dp)) {
                    Text("¿Dónde abordas?", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, fontSize = 14.sp)
                    Text("Este viaje tiene varios puntos de salida. Selecciona el tuyo.", color = Color.Gray, fontSize = 11.sp)
                    Spacer(modifier = Modifier.height(8.dp))
                    // FlowRow, no Row: con 3+ puntos un Row deja los últimos fuera de pantalla
                    // y comprime los demás hasta partir el texto en muchas líneas.
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        puntosSubidaViaje.forEach { punto ->
                            val info = puntosSubidaInfoAll.find { it.nombre == punto }
                            val seleccionado = puntoSubidaActual == punto
                            FilterChip(
                                selected = seleccionado,
                                onClick = { viewModel.seleccionarPuntoSubida(punto) },
                                label = {
                                    Column {
                                        Text(
                                            punto,
                                            fontSize = 13.sp,
                                            fontWeight = if (seleccionado) FontWeight.Bold else FontWeight.Normal,
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                        if (info?.hora?.isNotBlank() == true) {
                                            Text(info.hora, fontSize = 10.sp, color = if (seleccionado) SmartVanColors.AzulOscuro else Color.Gray)
                                        }
                                    }
                                },
                                leadingIcon = if (seleccionado) { { Icon(Icons.Filled.LocationOn, contentDescription = null, tint = SmartVanColors.Azul, modifier = Modifier.size(16.dp)) } } else null,
                                colors = FilterChipDefaults.filterChipColors(
                                    selectedContainerColor = SmartVanColors.Azul.copy(alpha = 0.2f)
                                )
                            )
                        }
                    }
                }
            }
        } else if (puntosSubidaViaje.size == 1) {
            val info = puntosSubidaInfoAll.find { it.nombre == puntosSubidaViaje.first() }
            if (info != null) {
                Card(
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp)
                ) {
                    Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Filled.LocationOn, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(18.dp))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Abordaje: ${info.nombre}${if (info.hora.isNotBlank()) " · ${info.hora}" else ""}", fontWeight = FontWeight.Medium, color = SmartVanColors.VerdeOscuro, fontSize = 13.sp)
                    }
                }
            }
        }

        Text("Selecciona tus asientos", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
        Spacer(modifier = Modifier.height(8.dp))

        Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
            LegendItem(SmartVanColors.Verde, "Libre")
            LegendItem(SmartVanColors.CyanAccent, "Tuyo")
            LegendItem(Color(0xFFD0D0D0), "Ocupado")
        }

        Spacer(modifier = Modifier.height(20.dp))

        // Van body
        if (isLoading) {
            Box(modifier = Modifier.width(300.dp).height(200.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = SmartVanColors.CyanAccent)
            }
        } else {
            // Mapa dinámico basado en layout del vehículo
            val layout = viewModel.layoutVehiculo.collectAsState().value
            val defaultLayout = VehiculoConfig(
                nombre = "Default", capacidad = 13, columnas = 4,
                filas = listOf(
                    FilaVehiculo(listOf(-1, 0, 0, 1)),
                    FilaVehiculo(listOf(2, 3, 0, 4)),
                    FilaVehiculo(listOf(5, 6, 0, 7)),
                    FilaVehiculo(listOf(8, 9, 0, 10)),
                    FilaVehiculo(listOf(11, 12, 13, 0)),
                )
            )
            val vehiculoLayout = layout ?: defaultLayout
            val seatMap = seats.associateBy { it.id }

            // Misma carrocería que el editor de vehículos y el panel de admin: es el MISMO
            // composable, así la vista previa del catálogo no puede desviarse de lo que ve
            // el cliente. Antes esta pantalla dibujaba su propia caja con el parabrisas
            // clavado arriba, y en una unidad con el volante en la última fila el frente
            // quedaba del lado contrario al conductor.
            CarroceriaVehiculo(
                filas = vehiculoLayout.filas,
                modifier = Modifier.widthIn(max = 320.dp)
            ) {
                vehiculoLayout.filas.forEach { fila ->
                    Row(
                        modifier = Modifier.width(280.dp),
                        horizontalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        fila.asientos.forEach { celda ->
                            Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                                when {
                                    celda == -1 -> {
                                        Box(modifier = Modifier.size(58.dp).background(Color(0xFF455A64), RoundedCornerShape(topStart = 14.dp, topEnd = 14.dp, bottomStart = 8.dp, bottomEnd = 8.dp)), contentAlignment = Alignment.Center) {
                                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                                Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = Color.White, modifier = Modifier.size(18.dp))
                                                Text("VOL", color = Color.White, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                                            }
                                        }
                                    }
                                    celda == 0 -> Spacer(modifier = Modifier.size(58.dp))
                                    else -> {
                                        val seat = seatMap[celda] ?: Seat(id = celda)
                                        SeatItem(seat) { viewModel.toggleSeatSelection(celda) }
                                    }
                                }
                            }
                        }
                    }
                    Spacer(modifier = Modifier.height(10.dp))
                }
            }
        }

        Spacer(modifier = Modifier.height(16.dp))

        // Total and includes
        Card(
            colors = CardDefaults.cardColors(containerColor = Color.White),
            shape = RoundedCornerShape(14.dp),
            elevation = CardDefaults.cardElevation(2.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text("Total:", style = MaterialTheme.typography.bodyLarge, color = Color.Gray)
                    Text("$$totalToPay MXN", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
                }
                HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp), color = Color.LightGray.copy(alpha = 0.4f))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    ServiceIcon(modifier = Modifier.weight(1f), text = "Aire Acondicionado", icon = Icons.Filled.AcUnit)
                    ServiceIcon(modifier = Modifier.weight(1f), text = "Unidades Cómodas", icon = Icons.Filled.DirectionsBus)
                    ServiceIcon(modifier = Modifier.weight(1f), text = "Equipaje 10kg", icon = Icons.Filled.Luggage)
                }
            }
        }

        Spacer(modifier = Modifier.height(16.dp))

        Button(
            onClick = { viewModel.intentarAvanzarYBloquear() },
            modifier = Modifier.fillMaxWidth().height(54.dp),
            colors = ButtonDefaults.buttonColors(containerColor = if (selectedSeatsCount > 0) SmartVanColors.CyanAccent else Color.LightGray),
            enabled = selectedSeatsCount > 0,
            shape = RoundedCornerShape(14.dp)
        ) {
            Text("Continuar con $selectedSeatsCount ${if (selectedSeatsCount == 1) "Asiento" else "Asientos"}", fontWeight = FontWeight.Bold, color = Color.White)
        }
        if (mostrarAviso10Minutos) {
            AlertDialog(
                onDismissRequest = { },
                title = { Text("Asientos Asegurados", fontWeight = FontWeight.Black) },
                text = { Text("Tus asientos han sido bloqueados para ti. Tienes exactamente 10 minutos para completar los datos de los pasajeros y realizar tu pago antes de que se liberen para otros clientes.") },
                confirmButton = {
                    Button(onClick = { viewModel.cerrarAvisoEIrAManifiesto() }, colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro)) {
                        Text("Entendido, Continuar")
                    }
                }
            )
        }

        if (errorAsientosGanados) {
            AlertDialog(
                onDismissRequest = { viewModel.cerrarErrorAsientos() },
                title = { Text("Asientos ya no disponibles", fontWeight = FontWeight.Black) },
                text = { Text("Lo sentimos, alguien más acaba de reservar uno de los asientos que elegiste en este mismo instante. Por favor, selecciona asientos diferentes.") },
                confirmButton = {
                    Button(onClick = { viewModel.cerrarErrorAsientos() }, colors = ButtonDefaults.buttonColors(containerColor = Color.Red)) {
                        Text("Aceptar")
                    }
                }
            )
        }
        if (sesionExpirada) {
            AlertDialog(
                onDismissRequest = { viewModel.cerrarAvisoSesionExpirada() },
                title = { Text("Tu selección expiró", fontWeight = FontWeight.Black) },
                text = {
                    Text("Cerraste o abandonaste la app y tus asientos fueron liberados para otros clientes. Por favor, vuelve a seleccionar tus asientos.")
                },
                confirmButton = {
                    Button(
                        onClick = { viewModel.cerrarAvisoSesionExpirada() },
                        colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro)
                    ) {
                        Text("Entendido")
                    }
                }
            )
        }
    }
}
@Composable
fun ManifiestoPasajerosView(viewModel: BookingViewModel) {
    val uriHandler = LocalUriHandler.current
    val isProcessing by viewModel.isProcessing.collectAsState()
    val origen by viewModel.origen.collectAsState()
    val destino by viewModel.destino.collectAsState()
    val fecha by viewModel.fecha.collectAsState()
    val horario by viewModel.horario.collectAsState()

    Column(modifier = Modifier.fillMaxSize()) {
        // Trip context
        Card(
            colors = CardDefaults.cardColors(containerColor = SmartVanColors.AzulOscuro),
            shape = RoundedCornerShape(14.dp),
            modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(14.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = SmartVanColors.CyanAccent, modifier = Modifier.size(20.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Column {
                        Text("$origen - $destino", color = Color.White, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyMedium)
                        Text("$fecha | $horario", color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall)
                    }
                }
                TextButton(onClick = { viewModel.retrocederAAsientos() }) {
                    Text("Cambiar", color = SmartVanColors.CyanAccent, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
                }
            }
        }

        Text(
            "Datos de Pasajeros",
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Black,
            color = SmartVanColors.AzulOscuro
        )
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            "Llena los datos de cada pasajero para generar los boletos.",
            color = Color.Gray,
            style = MaterialTheme.typography.bodySmall
        )
        Spacer(modifier = Modifier.height(12.dp))

        Box(modifier = Modifier.weight(1f)) {
            Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                PassengerForm(viewModel)
            }
        }

        Spacer(modifier = Modifier.height(12.dp))

        val errorFormulario by viewModel.errorFormulario.collectAsState()

        if (errorFormulario != null) {
            Card(
                colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo),
                shape = RoundedCornerShape(10.dp),
                modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp)
            ) {
                Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.Warning, contentDescription = null, tint = Color.Red, modifier = Modifier.size(18.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(errorFormulario!!, color = Color.Red, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold)
                }
            }
        }

        Button(
            onClick = { viewModel.validarYAvanzarAResumen() },
            modifier = Modifier.fillMaxWidth().height(54.dp),
            colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.CyanAccent),
            shape = RoundedCornerShape(14.dp)
        ) {
            Text("Revisar Compra", fontWeight = FontWeight.Bold, color = Color.White)
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ResumenCompraView(viewModel: BookingViewModel, totalToPay: Int, onIrAPagar: () -> Unit) {
    val origen by viewModel.origen.collectAsState()
    val destino by viewModel.destino.collectAsState()
    val fecha by viewModel.fecha.collectAsState()
    val horario by viewModel.horario.collectAsState()
    val puntoSubida by viewModel.puntoSubida.collectAsState()
    val esIdaYVuelta by viewModel.esIdaYVuelta.collectAsState()
    val fechaVuelta by viewModel.fechaVuelta.collectAsState()
    val horarioVuelta by viewModel.horarioVuelta.collectAsState()
    val pasajerosMap by viewModel.pasajeros.collectAsState()
    val precios by viewModel.precios.collectAsState()
    val isProcessing by viewModel.isProcessing.collectAsState()
    val requiereFactura by viewModel.requiereFactura.collectAsState()
    val rfcFactura by viewModel.rfcFactura.collectAsState()
    val razonSocialFactura by viewModel.razonSocialFactura.collectAsState()
    val regimenFiscalFactura by viewModel.regimenFiscalFactura.collectAsState()
    val usoCfdiFactura by viewModel.usoCfdiFactura.collectAsState()
    val cpFiscalFactura by viewModel.cpFiscalFactura.collectAsState()
    val precioBase = precios["base"] ?: 350
    val precioMaleta = precios["maleta"] ?: 150
    val precioMascota = precios["mascota"] ?: 150

    // Subtotal REAL ida+vuelta (extras propios por tramo). El servidor recalcula el cargo.
    val subtotalSinIva = viewModel.subtotalEstimado().let { if (it > 0) it else totalToPay }
    val ivaAmount = if (requiereFactura) (subtotalSinIva * 0.16).toInt() else 0
    val totalConFactura = if (requiereFactura) subtotalSinIva + ivaAmount else subtotalSinIva

    Column(modifier = Modifier.fillMaxSize()) {
        Text("Confirma tu Viaje", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
        Spacer(modifier = Modifier.height(12.dp))

        Card(
            modifier = Modifier.fillMaxWidth().weight(1f),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            elevation = CardDefaults.cardElevation(4.dp),
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(modifier = Modifier.padding(20.dp).verticalScroll(rememberScrollState())) {
                // ═══ TRAMO DE IDA ═══
                TramoResumen(
                    etiqueta = if (esIdaYVuelta) "IDA" else null,
                    etiquetaColor = SmartVanColors.CyanAccent,
                    ruta = "$origen - $destino",
                    fechaHora = "$fecha | $horario",
                    puntoSubida = viewModel.puntoSubidaIdaResumen(),
                    pasajeros = viewModel.pasajerosIdaResumen(),
                    precioBase = precioBase, precioMaleta = precioMaleta, precioMascota = precioMascota
                )

                // ═══ TRAMO DE VUELTA ═══
                if (esIdaYVuelta) {
                    Spacer(modifier = Modifier.height(8.dp))
                    HorizontalDivider(color = Color.LightGray.copy(alpha = 0.3f))
                    Spacer(modifier = Modifier.height(12.dp))
                    TramoResumen(
                        etiqueta = "VUELTA",
                        etiquetaColor = SmartVanColors.Verde,
                        ruta = "$destino - $origen",
                        fechaHora = "$fechaVuelta | $horarioVuelta",
                        puntoSubida = viewModel.puntoSubidaVueltaResumen(),
                        pasajeros = viewModel.pasajerosVueltaResumen(),
                        precioBase = precioBase, precioMaleta = precioMaleta, precioMascota = precioMascota
                    )
                }

                HorizontalDivider(modifier = Modifier.padding(vertical = 16.dp), color = Color.LightGray.copy(alpha = 0.4f))

                HorizontalDivider(modifier = Modifier.padding(vertical = 12.dp), color = Color.LightGray.copy(alpha = 0.4f))

                if (esIdaYVuelta) {
                    // Totales EXACTOS por tramo (cada uno con sus propios extras: maletas/mascota)
                    val totalIdaTramo = viewModel.totalTramoIda()
                    val totalVueltaTramo = viewModel.totalTramoVuelta()
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        val maletasIda = viewModel.pasajerosIdaResumen().sumOf { it.maletasExtras }
                        Text("Ida${if (maletasIda > 0) " (incluye $maletasIda maleta(s))" else ""}:", color = Color.Gray, style = MaterialTheme.typography.bodyMedium)
                        Text("$$totalIdaTramo MXN", color = Color.Gray, style = MaterialTheme.typography.bodyMedium)
                    }
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        val maletasVlt = viewModel.pasajerosVueltaResumen().sumOf { it.maletasExtras }
                        Text("Vuelta${if (maletasVlt > 0) " (incluye $maletasVlt maleta(s))" else ""}:", color = Color.Gray, style = MaterialTheme.typography.bodyMedium)
                        Text("$$totalVueltaTramo MXN", color = Color.Gray, style = MaterialTheme.typography.bodyMedium)
                    }
                    Spacer(modifier = Modifier.height(4.dp))
                }

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text(if (esIdaYVuelta) "Subtotal (Ida + Vuelta):" else "Subtotal:", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyLarge, color = Color.Gray)
                    Text("$$subtotalSinIva MXN", fontWeight = FontWeight.Black, style = MaterialTheme.typography.headlineSmall, color = SmartVanColors.AzulOscuro)
                }

                // ═══ Sección Facturación ═══
                Spacer(modifier = Modifier.height(16.dp))
                HorizontalDivider(color = Color.LightGray.copy(alpha = 0.4f))
                Spacer(modifier = Modifier.height(12.dp))

                Row(
                    modifier = Modifier.fillMaxWidth().clickable { viewModel.toggleRequiereFactura(!requiereFactura) },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Checkbox(
                        checked = requiereFactura,
                        onCheckedChange = null, // El Row ya maneja el clic, evitar doble toggle
                        colors = CheckboxDefaults.colors(checkedColor = SmartVanColors.CyanAccent)
                    )
                    Icon(Icons.Filled.Receipt, contentDescription = null, tint = if (requiereFactura) SmartVanColors.CyanAccent else Color.Gray, modifier = Modifier.size(20.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Column {
                        Text("Necesito factura (CFDI)", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodyMedium)
                        Text("Se agregará IVA (16%) al costo total", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    }
                }

                if (requiereFactura) {
                    Spacer(modifier = Modifier.height(12.dp))

                    Card(
                        colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            "Al solicitar factura, el costo del boleto será precio real + IVA (16%). Ingresa tus datos fiscales para emitir tu CFDI.",
                            style = MaterialTheme.typography.bodySmall,
                            color = Color(0xFF6D4C00),
                            modifier = Modifier.padding(12.dp)
                        )
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    Text("DATOS FISCALES", style = MaterialTheme.typography.labelSmall, color = SmartVanColors.CyanAccent, fontWeight = FontWeight.Bold)
                    Spacer(modifier = Modifier.height(8.dp))

                    val rfcValido = rfcFactura.length in 12..13 && rfcFactura.all { it.isLetterOrDigit() }
                    OutlinedTextField(
                        value = rfcFactura,
                        onValueChange = { viewModel.setRfcFactura(it.uppercase().filter { c -> c.isLetterOrDigit() }.take(13)) },
                        label = { Text("RFC") },
                        placeholder = { Text("XAXX010101000") },
                        leadingIcon = { Icon(Icons.Filled.Shield, contentDescription = null, modifier = Modifier.size(18.dp)) },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp),
                        isError = rfcFactura.isNotBlank() && !rfcValido,
                        supportingText = if (rfcFactura.isNotBlank() && !rfcValido) {
                            { Text("RFC debe tener 12 (moral) o 13 (física) caracteres alfanuméricos", color = Color.Red, style = MaterialTheme.typography.labelSmall) }
                        } else null
                    )
                    Spacer(modifier = Modifier.height(8.dp))

                    OutlinedTextField(
                        value = razonSocialFactura,
                        onValueChange = { viewModel.setRazonSocialFactura(it) },
                        label = { Text("Razón Social") },
                        placeholder = { Text("Nombre o empresa") },
                        leadingIcon = { Icon(Icons.Filled.Person, contentDescription = null, modifier = Modifier.size(18.dp)) },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp)
                    )
                    Spacer(modifier = Modifier.height(8.dp))

                    // Dropdown Régimen Fiscal (códigos SAT)
                    val regimenesFiscales = listOf(
                        "601 - General de Ley Personas Morales",
                        "603 - Personas Morales sin Fines de Lucro",
                        "605 - Sueldos y Salarios",
                        "606 - Arrendamiento",
                        "608 - Demás Ingresos",
                        "610 - Residentes en el Extranjero",
                        "611 - Ingresos por Dividendos",
                        "612 - Personas Físicas con Actividades Empresariales y Profesionales",
                        "614 - Ingresos por Intereses",
                        "616 - Sin Obligaciones Fiscales",
                        "620 - Sociedades Cooperativas de Producción",
                        "621 - Incorporación Fiscal",
                        "622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
                        "623 - Opcional para Grupos de Sociedades",
                        "624 - Coordinados",
                        "625 - Régimen de las Actividades Empresariales (Plataformas Tecnológicas)",
                        "626 - Régimen Simplificado de Confianza"
                    )
                    var expandedRegimen by remember { mutableStateOf(false) }
                    ExposedDropdownMenuBox(
                        expanded = expandedRegimen,
                        onExpandedChange = { expandedRegimen = it },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        OutlinedTextField(
                            value = regimenFiscalFactura,
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Régimen Fiscal") },
                            placeholder = { Text("Selecciona régimen") },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedRegimen) },
                            modifier = Modifier.fillMaxWidth().menuAnchor(),
                            singleLine = true,
                            shape = RoundedCornerShape(10.dp)
                        )
                        ExposedDropdownMenu(expanded = expandedRegimen, onDismissRequest = { expandedRegimen = false }) {
                            regimenesFiscales.forEach { regimen ->
                                DropdownMenuItem(
                                    text = { Text(regimen, style = MaterialTheme.typography.bodySmall) },
                                    onClick = {
                                        viewModel.setRegimenFiscalFactura(regimen)
                                        expandedRegimen = false
                                    }
                                )
                            }
                        }
                    }
                    Spacer(modifier = Modifier.height(8.dp))

                    // Dropdown Uso CFDI (códigos SAT)
                    val usosCfdi = listOf(
                        "G01 - Adquisición de mercancías",
                        "G02 - Devoluciones, descuentos o bonificaciones",
                        "G03 - Gastos en general",
                        "I01 - Construcciones",
                        "I02 - Mobiliario y equipo de oficina",
                        "I03 - Equipo de transporte",
                        "I04 - Equipo de cómputo",
                        "I08 - Otra maquinaria y equipo",
                        "D01 - Honorarios médicos, dentales y gastos hospitalarios",
                        "D02 - Gastos médicos por incapacidad o discapacidad",
                        "D03 - Gastos funerales",
                        "D04 - Donativos",
                        "D05 - Intereses reales de créditos hipotecarios",
                        "D06 - Aportaciones voluntarias al SAR",
                        "D07 - Primas por seguros de gastos médicos",
                        "D08 - Gastos de transportación escolar",
                        "D10 - Pagos por servicios educativos",
                        "S01 - Sin efectos fiscales",
                        "CP01 - Pagos"
                    )
                    var expandedUso by remember { mutableStateOf(false) }
                    ExposedDropdownMenuBox(
                        expanded = expandedUso,
                        onExpandedChange = { expandedUso = it },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        OutlinedTextField(
                            value = usoCfdiFactura,
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Uso del CFDI") },
                            placeholder = { Text("Selecciona uso") },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expandedUso) },
                            modifier = Modifier.fillMaxWidth().menuAnchor(),
                            singleLine = true,
                            shape = RoundedCornerShape(10.dp)
                        )
                        ExposedDropdownMenu(expanded = expandedUso, onDismissRequest = { expandedUso = false }) {
                            usosCfdi.forEach { uso ->
                                DropdownMenuItem(
                                    text = { Text(uso, style = MaterialTheme.typography.bodySmall) },
                                    onClick = {
                                        viewModel.setUsoCfdiFactura(uso)
                                        expandedUso = false
                                    }
                                )
                            }
                        }
                    }
                    Spacer(modifier = Modifier.height(8.dp))

                    OutlinedTextField(
                        value = cpFiscalFactura,
                        onValueChange = { viewModel.setCpFiscalFactura(it.filter { c -> c.isDigit() }.take(5)) },
                        label = { Text("Código Postal Fiscal") },
                        placeholder = { Text("Ej: 44100") },
                        leadingIcon = { Icon(Icons.Filled.LocationOn, contentDescription = null, modifier = Modifier.size(18.dp)) },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        shape = RoundedCornerShape(10.dp),
                        isError = cpFiscalFactura.isNotBlank() && cpFiscalFactura.length != 5,
                        supportingText = if (cpFiscalFactura.isNotBlank() && cpFiscalFactura.length != 5) {
                            { Text("El C.P. debe tener 5 dígitos", color = Color.Red, style = MaterialTheme.typography.labelSmall) }
                        } else null
                    )

                    Spacer(modifier = Modifier.height(12.dp))

                    // Desglose con IVA
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("Subtotal:", style = MaterialTheme.typography.bodyMedium, color = Color.Gray)
                        Text("$$subtotalSinIva MXN", style = MaterialTheme.typography.bodyMedium, color = Color.Gray)
                    }
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("IVA (16%):", style = MaterialTheme.typography.bodyMedium, color = Color.Gray)
                        Text("$$ivaAmount MXN", style = MaterialTheme.typography.bodyMedium, color = Color.Gray)
                    }
                    Spacer(modifier = Modifier.height(4.dp))
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                        Text("TOTAL CON FACTURA:", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyLarge, color = SmartVanColors.AzulOscuro)
                        Text("$$totalConFactura MXN", fontWeight = FontWeight.Black, style = MaterialTheme.typography.headlineSmall, color = SmartVanColors.Verde)
                    }
                }
            }
        }

        Spacer(modifier = Modifier.height(12.dp))

        TextButton(onClick = { viewModel.retrocederAlManifiesto() }, modifier = Modifier.align(Alignment.CenterHorizontally)) {
            Text("Editar datos de pasajeros", color = Color.Gray)
        }

        Spacer(modifier = Modifier.height(4.dp))

        // Checkbox de términos y condiciones
        var aceptaTerminos by remember { mutableStateOf(false) }
        var mostrarTerminosResumen by remember { mutableStateOf(false) }
        var mostrarPrivacidadResumen by remember { mutableStateOf(false) }
        var errorFactura by remember { mutableStateOf(false) }

        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)
        ) {
            Checkbox(
                checked = aceptaTerminos,
                onCheckedChange = { aceptaTerminos = it },
                colors = CheckboxDefaults.colors(checkedColor = SmartVanColors.CyanAccent)
            )
            Column {
                Row {
                    Text("Acepto los ", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                    Text(
                        "Términos y Condiciones",
                        style = MaterialTheme.typography.bodySmall,
                        color = SmartVanColors.CyanAccent,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.clickable { mostrarTerminosResumen = true }
                    )
                }
                Row {
                    Text("y el ", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                    Text(
                        "Aviso de Privacidad",
                        style = MaterialTheme.typography.bodySmall,
                        color = SmartVanColors.CyanAccent,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.clickable { mostrarPrivacidadResumen = true }
                    )
                }
            }
        }

        if (mostrarTerminosResumen) {
            TerminosYCondicionesDialog(onDismiss = { mostrarTerminosResumen = false })
        }
        if (mostrarPrivacidadResumen) {
            AvisoDePrivacidadDialog(onDismiss = { mostrarPrivacidadResumen = false })
        }

        Spacer(modifier = Modifier.height(4.dp))

        if (errorFactura) {
            Card(
                colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp)
            ) {
                Text(
                    "Completa correctamente los datos fiscales: RFC válido (12-13 caracteres), Razón Social, Régimen Fiscal, Uso CFDI y C.P. de 5 dígitos.",
                    color = Color.Red,
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.padding(12.dp)
                )
            }
        }

        Button(
            onClick = {
                if (requiereFactura) {
                    val rfcValidoGate = rfcFactura.length in 12..13 && rfcFactura.all { it.isLetterOrDigit() }
                    val cpValidoGate = cpFiscalFactura.length == 5 && cpFiscalFactura.all { it.isDigit() }
                    val datosIncompletos = rfcFactura.isBlank() || razonSocialFactura.isBlank() ||
                        regimenFiscalFactura.isBlank() || usoCfdiFactura.isBlank() || cpFiscalFactura.isBlank() ||
                        !rfcValidoGate || !cpValidoGate
                    if (datosIncompletos) {
                        errorFactura = true
                        return@Button
                    }
                }
                errorFactura = false
                viewModel.confirmarPagoYReservar {
                    onIrAPagar()
                }
            },
            modifier = Modifier.fillMaxWidth().height(54.dp),
            colors = ButtonDefaults.buttonColors(containerColor = if (aceptaTerminos) SmartVanColors.CyanAccent else Color.LightGray),
            enabled = aceptaTerminos,
            shape = RoundedCornerShape(14.dp)
        ) {
            if (isProcessing) {
                CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp))
            } else {
                Text("Elegir Método de Pago — $$totalConFactura MXN", fontWeight = FontWeight.Bold, color = Color.White)
            }
        }
    }
}

/** Resumen completo de un tramo: ruta + abordaje + cada pasajero con sube/baja/asiento/extras/precio. */
@Composable
private fun TramoResumen(
    etiqueta: String?,
    etiquetaColor: Color,
    ruta: String,
    fechaHora: String,
    puntoSubida: String,
    pasajeros: List<PasajeroInfo>,
    precioBase: Int, precioMaleta: Int, precioMascota: Int
) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = etiquetaColor, modifier = Modifier.size(24.dp))
        Spacer(modifier = Modifier.width(10.dp))
        Column {
            if (etiqueta != null) Text(etiqueta, style = MaterialTheme.typography.labelSmall, color = etiquetaColor, fontWeight = FontWeight.Bold)
            Text(ruta, fontWeight = FontWeight.Black, style = MaterialTheme.typography.titleMedium, color = SmartVanColors.AzulOscuro)
            Text(fechaHora, fontWeight = FontWeight.Medium, color = Color.Gray, style = MaterialTheme.typography.bodyMedium)
        }
    }
    if (puntoSubida.isNotBlank()) {
        Card(colors = CardDefaults.cardColors(containerColor = Color(0xFFE3F2FD)), shape = RoundedCornerShape(8.dp), modifier = Modifier.fillMaxWidth().padding(top = 8.dp)) {
            Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Filled.TripOrigin, contentDescription = null, tint = SmartVanColors.Azul, modifier = Modifier.size(16.dp))
                Spacer(modifier = Modifier.width(8.dp))
                Column {
                    Text("ABORDAJE (todos los pasajeros)", style = MaterialTheme.typography.labelSmall, color = Color(0xFF1565C0), fontWeight = FontWeight.Bold)
                    Text(puntoSubida, fontWeight = FontWeight.SemiBold, color = SmartVanColors.Azul)
                }
            }
        }
    }
    Spacer(modifier = Modifier.height(10.dp))
    Text("PASAJEROS (${pasajeros.size})", style = MaterialTheme.typography.labelSmall, color = etiquetaColor, fontWeight = FontWeight.Bold)
    Spacer(modifier = Modifier.height(8.dp))
    pasajeros.forEach { p ->
        val costoMaletas = p.maletasExtras * precioMaleta
        val costoMascotaP = if (p.llevaMascota) precioMascota else 0
        val subtotalP = precioBase + costoMaletas + costoMascotaP
        Card(colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoPanel), shape = RoundedCornerShape(10.dp), modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp)) {
            Column(modifier = Modifier.padding(12.dp)) {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Text(p.nombre, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyMedium)
                    Text("$$subtotalP", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodyMedium)
                }
                Text("Asiento ${p.asiento}", style = MaterialTheme.typography.bodySmall, color = Color.Gray, fontWeight = FontWeight.SemiBold)
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(top = 3.dp)) {
                    Icon(Icons.Filled.TripOrigin, contentDescription = null, tint = SmartVanColors.VerdeOscuro, modifier = Modifier.size(12.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text("Sube en: ${puntoSubida.ifBlank { "Por confirmar" }}", style = MaterialTheme.typography.labelSmall, color = SmartVanColors.VerdeOscuro, fontWeight = FontWeight.Medium)
                }
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.LocationOn, contentDescription = null, tint = Color(0xFFEF6C00), modifier = Modifier.size(12.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text("Baja en: ${p.parada.ifBlank { "Por confirmar" }}", style = MaterialTheme.typography.labelSmall, color = Color(0xFFEF6C00), fontWeight = FontWeight.Medium)
                }
                Spacer(modifier = Modifier.height(6.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Text("Boleto base", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    Text("$$precioBase", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                }
                if (p.maletasExtras > 0) {
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("${p.maletasExtras} maleta(s) extra", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                        Text("$$costoMaletas", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    }
                }
                if (p.llevaMascota) {
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("Mascota", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                        Text("$$costoMascotaP", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    }
                }
            }
        }
    }
}

@Composable
fun PassengerForm(viewModel: BookingViewModel) {
    val pasajerosMap by viewModel.pasajeros.collectAsState()
    val precios by viewModel.precios.collectAsState()
    val paradas = viewModel.getParadasFiltradas()
    val precioMaleta = precios["maleta"] ?: 150
    val precioMascota = precios["mascota"] ?: 150
    val selectedSeats = pasajerosMap.keys.toList().sorted()

    Column(modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp)) {
        selectedSeats.forEachIndexed { index, seatId ->
            val pasajero = pasajerosMap[seatId] ?: PasajeroInfo(asiento = seatId)
            val isTitular = index == 0

            PassengerCard(seatId = seatId, isTitular = isTitular, pasajero = pasajero, precioMaleta = precioMaleta, precioMascota = precioMascota, paradas = paradas, onUpdate = { nuevaInfo -> viewModel.updatePassengerInfo(seatId, nuevaInfo) })
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PassengerCard(seatId: Int, isTitular: Boolean, pasajero: PasajeroInfo, precioMaleta: Int = 150, precioMascota: Int = 150, paradas: List<String> = emptyList(), onUpdate: (PasajeroInfo) -> Unit) {
    var expanded by remember { mutableStateOf(false) }
    val stops = paradas

    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text(text = if (isTitular) "Pasajero 1 (Titular) - Asiento $seatId" else "Pasajero - Asiento $seatId", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, modifier = Modifier.padding(bottom = 8.dp))

            OutlinedTextField(value = pasajero.nombre, onValueChange = { onUpdate(pasajero.copy(nombre = it)) }, label = { Text("Nombre Completo (Identificación)") }, modifier = Modifier.fillMaxWidth(), singleLine = true)
            Spacer(modifier = Modifier.height(8.dp))

            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(
                    value = pasajero.telefono,
                    onValueChange = { nuevo ->
                        // Solo dígitos, máximo 10 (México). takeLast en vez de take: si pegan
                        // "+521234567890" queremos "1234567890", no "5212345678".
                        val filtrado = nuevo.filter { it.isDigit() }.takeLast(10)
                        onUpdate(pasajero.copy(telefono = filtrado))
                    },
                    label = { Text("Teléfono") },
                    modifier = Modifier.weight(1f),
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                    isError = pasajero.telefono.isNotBlank() && pasajero.telefono.length < 10,
                    supportingText = if (pasajero.telefono.isNotBlank() && pasajero.telefono.length < 10) {
                        { Text("10 dígitos requeridos", color = Color.Red, style = MaterialTheme.typography.labelSmall) }
                    } else null
                )

                ExposedDropdownMenuBox(
                    expanded = expanded,
                    onExpandedChange = { expanded = it },
                    modifier = Modifier.weight(1f)
                ) {
                    OutlinedTextField(
                        value = pasajero.parada.ifEmpty { "Selecciona..." },
                        onValueChange = {},
                        readOnly = true,
                        label = { Text("Baja en...") },
                        modifier = Modifier.fillMaxWidth().menuAnchor(),
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) }
                    )
                    ExposedDropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
                        stops.forEach { stop ->
                            DropdownMenuItem(
                                text = { Text(stop, fontWeight = FontWeight.Medium) },
                                onClick = { onUpdate(pasajero.copy(parada = stop)); expanded = false },
                                contentPadding = ExposedDropdownMenuDefaults.ItemContentPadding
                            )
                        }
                    }
                }
            }

            HorizontalDivider(modifier = Modifier.padding(vertical = 12.dp), color = Color.LightGray.copy(alpha = 0.5f))

            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Column {
                    Text("Maletas Extras ($$precioMaleta)", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(onClick = { if (pasajero.maletasExtras > 0) onUpdate(pasajero.copy(maletasExtras = pasajero.maletasExtras - 1)) }) { Text("-", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro) }
                        Text("${pasajero.maletasExtras}", fontWeight = FontWeight.Bold)
                        IconButton(onClick = { if (pasajero.maletasExtras < 2) onUpdate(pasajero.copy(maletasExtras = pasajero.maletasExtras + 1)) }) { Text("+", fontWeight = FontWeight.Bold, color = if (pasajero.maletasExtras < 2) SmartVanColors.AzulOscuro else Color.LightGray) }
                    }
                }
                Column(horizontalAlignment = Alignment.End) {
                    Text("Mascota ($$precioMascota)", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    Switch(checked = pasajero.llevaMascota, onCheckedChange = { onUpdate(pasajero.copy(llevaMascota = it)) }, colors = SwitchDefaults.colors(checkedThumbColor = SmartVanColors.AzulOscuro))
                }
            }

            if (isTitular) {
                Spacer(modifier = Modifier.height(8.dp))
                val emailInvalido = pasajero.correo.isNotBlank() && !pasajero.correo.contains("@")

                OutlinedTextField(
                    value = pasajero.correo,
                    onValueChange = { onUpdate(pasajero.copy(correo = it)) },
                    label = { Text("Correo (Aquí llegarán los boletos PDF)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    isError = emailInvalido,
                    supportingText = if (emailInvalido) {{ Text("Ingresa un correo válido", color = Color.Red) }} else null,
                )
            }
        }
    }
}

@Composable
fun DateCarousel(selectedDate: String, onDateSelected: (String) -> Unit) {
    val fechaHoy = remember { org.example.abigail.obtenerFechaHoy() }

    val fechasDisponibles = remember {
        generarFechasPrueba().filter { it.fechaCompleta >= fechaHoy }
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        Text("¿Cuándo viajas?", fontWeight = FontWeight.Bold, color = Color.DarkGray, modifier = Modifier.padding(bottom = 8.dp))

        CarruselHorizontal(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            items(fechasDisponibles.size) { index ->
                val fecha = fechasDisponibles[index]
                val isSelected = fecha.fechaCompleta == selectedDate

                Card(
                    colors = CardDefaults.cardColors(containerColor = if (isSelected) SmartVanColors.AzulOscuro else Color(0xFFF3F4F6)),
                    modifier = Modifier.width(70.dp).height(80.dp).clickable { onDateSelected(fecha.fechaCompleta) },
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Column(modifier = Modifier.fillMaxSize(), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                        Text(text = fecha.diaSemana, style = MaterialTheme.typography.bodySmall, color = if (isSelected) Color.White.copy(alpha = 0.8f) else Color.Gray)
                        Text(text = fecha.diaNumero, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Black, color = if (isSelected) Color.White else Color.DarkGray)
                        Text(text = fecha.mes, style = MaterialTheme.typography.labelSmall, color = if (isSelected) Color.White.copy(alpha = 0.8f) else Color.Gray)
                    }
                }
            }
        }
    }
}

@Composable
fun SeatItem(seat: Seat, onClick: () -> Unit) {
    val backgroundColor = when (seat.status) {
        SeatStatus.AVAILABLE -> SmartVanColors.Verde
        SeatStatus.SELECTED -> SmartVanColors.CyanAccent
        SeatStatus.OCCUPIED -> Color(0xFFD0D0D0)
    }
    val borderColor = when (seat.status) {
        SeatStatus.AVAILABLE -> Color(0xFF388E3C)
        SeatStatus.SELECTED -> Color(0xFF00838F)
        SeatStatus.OCCUPIED -> Color(0xFFBDBDBD)
    }
    Column(
        modifier = Modifier.clickable(enabled = seat.status != SeatStatus.OCCUPIED, onClick = onClick),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Headrest
        Box(
            modifier = Modifier
                .width(40.dp)
                .height(8.dp)
                .background(backgroundColor, RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp))
        )
        // Seat body
        Box(
            modifier = Modifier
                .size(58.dp)
                .background(backgroundColor, RoundedCornerShape(6.dp))
                .then(
                    if (seat.status == SeatStatus.SELECTED)
                        Modifier.background(backgroundColor, RoundedCornerShape(6.dp))
                    else Modifier
                ),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = seat.id.toString(),
                color = if (seat.status == SeatStatus.OCCUPIED) Color.Gray else Color.White,
                fontWeight = FontWeight.Bold,
                style = MaterialTheme.typography.titleMedium
            )
        }
    }
}

@Composable
fun LegendItem(color: Color, text: String) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(modifier = Modifier.size(16.dp).background(color, RoundedCornerShape(4.dp)))
        Spacer(modifier = Modifier.width(4.dp))
        Text(text, style = MaterialTheme.typography.bodySmall)
    }
}

@Composable
fun ServiceIcon(modifier: Modifier = Modifier, text: String, icon: androidx.compose.ui.graphics.vector.ImageVector? = null) {
    Row(modifier = modifier, verticalAlignment = Alignment.CenterVertically) {
        if (icon != null) {
            Icon(icon, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(16.dp))
        } else {
            Canvas(modifier = Modifier.size(14.dp)) {
                val path = Path().apply { moveTo(size.width * 0.15f, size.height * 0.5f); lineTo(size.width * 0.4f, size.height * 0.8f); lineTo(size.width * 0.85f, size.height * 0.2f) }
                drawPath(path = path, color = SmartVanColors.AzulOscuro, style = Stroke(width = 2.dp.toPx(), cap = StrokeCap.Round, join = StrokeJoin.Round))
            }
        }
        Spacer(modifier = Modifier.width(6.dp))
        // Permitir 2 líneas: rutas/direcciones largas se envuelven en vez de cortarse a media pantalla.
        Text(text, style = MaterialTheme.typography.bodySmall, maxLines = 2, overflow = TextOverflow.Ellipsis)
    }
}

data class DiaViaje(val diaSemana: String, val diaNumero: String, val mes: String, val fechaCompleta: String)

fun generarFechasPrueba(): List<DiaViaje> {
    val diasSemana = listOf("Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom")
    val meses = listOf("Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic")
    val lista = mutableListOf<DiaViaje>()

    val fechaHoy = org.example.abigail.obtenerFechaHoy()
    val partes = fechaHoy.split("-")
    var anio = partes[0].toInt()
    var mes = partes[1].toInt()
    var dia = partes[2].toInt()

    var diaSemanaIdx = calcularDiaSemana(anio, mes, dia)

    for (i in 0 until 30) {
        val fechaGuardar = "$anio-${mes.toString().padStart(2, '0')}-${dia.toString().padStart(2, '0')}"
        lista.add(
            DiaViaje(
                diaSemana = diasSemana[diaSemanaIdx],
                diaNumero = dia.toString(),
                mes = meses[mes - 1],
                fechaCompleta = fechaGuardar
            )
        )

        dia++
        diaSemanaIdx = (diaSemanaIdx + 1) % 7

        val diasEnMes = when (mes) {
            1, 3, 5, 7, 8, 10, 12 -> 31
            4, 6, 9, 11 -> 30
            2 -> if (anio % 4 == 0 && (anio % 100 != 0 || anio % 400 == 0)) 29 else 28
            else -> 30
        }

        if (dia > diasEnMes) {
            dia = 1
            mes++
            if (mes > 12) {
                mes = 1
                anio++
            }
        }
    }

    return lista
}

/**
 * Calcula el día de la semana (0=Lun, 1=Mar, ..., 6=Dom)
 * usando la fórmula de Tomohiko Sakamoto
 */
fun calcularDiaSemana(anio: Int, mes: Int, dia: Int): Int {
    val t = intArrayOf(0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4)
    var y = anio
    if (mes < 3) y--
    val dow = (y + y / 4 - y / 100 + y / 400 + t[mes - 1] + dia) % 7
    return if (dow == 0) 6 else dow - 1
}

@Composable
fun BoletosDigitalesView(viewModel: BookingViewModel) {
    val boleto by viewModel.ultimoBoleto.collectAsState()
    val waUrl by viewModel.whatsappUrl.collectAsState()
    val precios by viewModel.precios.collectAsState()
    val uriHandler = LocalUriHandler.current

    if (boleto == null) return

    Column(modifier = Modifier.fillMaxSize(), horizontalAlignment = Alignment.CenterHorizontally) {
        Box(
            modifier = Modifier
                .size(64.dp)
                .background(SmartVanColors.Verde.copy(alpha = 0.1f), CircleShape),
            contentAlignment = Alignment.Center
        ) {
            Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(40.dp))
        }
        Spacer(modifier = Modifier.height(12.dp))
        Text("¡Reserva Exitosa!", style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.Black, color = SmartVanColors.Verde)
        Text("Aquí están tus pases de abordar", color = Color.Gray, modifier = Modifier.padding(bottom = 16.dp))

        LazyColumn(modifier = Modifier.weight(1f).fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            items(boleto!!.pasajeros) { pasajero ->
                TicketCardVisual(boleto = boleto!!, pasajero = pasajero, precios = precios)
            }
        }

        Spacer(modifier = Modifier.height(16.dp))

        if (waUrl != null) {
            Button(
                onClick = { uriHandler.openUri(waUrl!!) },
                modifier = Modifier.fillMaxWidth().height(55.dp),
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF25D366))
            ) {
                Text("Enviar Confirmación por WhatsApp", fontWeight = FontWeight.Bold, color = Color.White)
            }
            Spacer(modifier = Modifier.height(8.dp))
        }

        Button(onClick = { viewModel.finalizarCompraYSalir() }, modifier = Modifier.fillMaxWidth().height(55.dp), colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro)) {
            Text("Entendido, volver al inicio", fontWeight = FontWeight.Bold, color = Color.White)
        }
    }
}
@Composable
fun TicketCardVisual(boleto: Boleto, pasajero: PasajeroInfo, precios: Map<String, Int> = emptyMap()) {
    val costoBase = precios["base"] ?: 350
    val costoMaletas = pasajero.maletasExtras * (precios["maleta"] ?: 150)
    val costoMascota = if (pasajero.llevaMascota) (precios["mascota"] ?: 150) else 0
    val totalPasajero = costoBase + costoMaletas + costoMascota

    Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = Color.White), elevation = CardDefaults.cardElevation(defaultElevation = 6.dp), shape = RoundedCornerShape(16.dp)) {
        Column {
            Box(modifier = Modifier.fillMaxWidth().background(SmartVanColors.AzulOscuro).padding(12.dp)) {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Text("SMARTVAN PASS", color = Color.White, fontWeight = FontWeight.Black)
                    Text("ASIENTO ${pasajero.asiento}", color = Color.White, fontWeight = FontWeight.Bold)
                }
            }
            Column(modifier = Modifier.padding(16.dp)) {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Column {
                        Text("PASAJERO", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                        Text(pasajero.nombre.uppercase(), fontWeight = FontWeight.Bold)
                    }
                    Column(horizontalAlignment = Alignment.End) {
                        Text("SALIDA", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                        Text("${boleto.fechaViaje} | ${boleto.horario}", fontWeight = FontWeight.Bold)
                    }
                }
                Spacer(modifier = Modifier.height(10.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Column {
                        Text("ABORDAJE", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                        Text(boleto.puntoSubida.ifBlank { "Terminal" }, fontWeight = FontWeight.Bold, color = SmartVanColors.Azul)
                    }
                    if (pasajero.parada.isNotBlank()) {
                        Column(horizontalAlignment = Alignment.End) {
                            Text("BAJADA", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                            Text(pasajero.parada, fontWeight = FontWeight.Bold, color = Color(0xFFEF6C00))
                        }
                    }
                }
                HorizontalDivider(modifier = Modifier.padding(vertical = 12.dp), color = Color.LightGray.copy(alpha = 0.3f))
                Text("RESUMEN DE EQUIPAJE Y EXTRAS", style = MaterialTheme.typography.labelSmall, color = SmartVanColors.AzulOscuro, fontWeight = FontWeight.Bold)
                Row(modifier = Modifier.fillMaxWidth().padding(top = 4.dp), horizontalArrangement = Arrangement.SpaceBetween) {
                    Column {
                        if (pasajero.maletasExtras > 0) Text("• ${pasajero.maletasExtras} Maleta(s) Extra", style = MaterialTheme.typography.bodySmall)
                        if (pasajero.llevaMascota) Text("• Viaja con Mascota", style = MaterialTheme.typography.bodySmall)
                        if (pasajero.maletasExtras == 0 && !pasajero.llevaMascota) Text("• Equipaje estándar (10kg)", style = MaterialTheme.typography.bodySmall)
                    }
                    Text("Total: $$totalPasajero", fontWeight = FontWeight.Black, color = Color.DarkGray)
                }
                Spacer(modifier = Modifier.height(16.dp))

                // ID de Rastreo / Verificación
                val codigoViaje = "SMARTVAN-${boleto.timestamp}-${pasajero.asiento}"
                Card(
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFF1F8E9)),
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(12.dp)) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Filled.ConfirmationNumber, contentDescription = null, tint = SmartVanColors.VerdeOscuro, modifier = Modifier.size(18.dp))
                            Spacer(modifier = Modifier.width(6.dp))
                            Text("TU CÓDIGO DE VIAJE", style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold, color = SmartVanColors.VerdeOscuro)
                        }
                        Spacer(modifier = Modifier.height(6.dp))
                        Text(
                            codigoViaje,
                            fontWeight = FontWeight.Black,
                            color = SmartVanColors.AzulOscuro,
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.fillMaxWidth()
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        Text("Usa este código para rastrear tu viaje en vivo o para verificación al abordar", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))
                Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier.size(80.dp).background(SmartVanColors.FondoGrisClaro, RoundedCornerShape(12.dp)),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Icon(Icons.Filled.QrCode2, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(36.dp))
                            Text("Escanear", fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.labelSmall)
                        }
                    }
                    Spacer(modifier = Modifier.width(16.dp))
                    Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Button(
                            onClick = { imprimirBoleto(pasajero, boleto, totalPasajero) },
                            modifier = Modifier.fillMaxWidth(),
                            colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro),
                            shape = RoundedCornerShape(10.dp)
                        ) {
                            Icon(Icons.Filled.FileDownload, contentDescription = null, modifier = Modifier.size(18.dp))
                            Spacer(modifier = Modifier.width(6.dp))
                            Text("Descargar PDF", fontWeight = FontWeight.Bold, color = Color.White)
                        }
                        Text("Presenta al abordar", style = MaterialTheme.typography.labelSmall, color = Color.Gray, modifier = Modifier.align(Alignment.CenterHorizontally))
                    }
                }
            }
        }
    }
}

fun generarHtmlBoleto(pasajero: PasajeroInfo, boleto: Boleto, total: Int): String {
    // Mismo boleto que se manda por correo. Ver BoletoHtml.kt.
    val html = generarHtmlBoletoSmartVan(
        nombre = pasajero.nombre,
        asiento = pasajero.asiento.toString(),
        parada = pasajero.parada,
        telefono = pasajero.telefono,
        maletasExtras = pasajero.maletasExtras,
        llevaMascota = pasajero.llevaMascota,
        ruta = boleto.origenDestino,
        fecha = boleto.fechaViaje,
        horario = boleto.horario,
        totalPagado = total,
        codigoQR = boleto.codigoQR.ifBlank { "SMARTVAN-${boleto.timestamp}-${pasajero.asiento}" },
        puntoSubida = boleto.puntoSubida,
        horaSubida = boleto.horaSubida,
        mapsUrl = boleto.mapsUrl,
        mapsUrlBajada = boleto.mapsUrlBajada
    )
    return envolverBoletosParaImprimir(html)
}
@Composable
fun TerminosYCondicionesDialog(onDismiss: () -> Unit) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Filled.Gavel, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(24.dp))
                Spacer(modifier = Modifier.width(8.dp))
                Text("Términos y Condiciones", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
            }
        },
        text = {
            Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                // Fuente única compartida con la hoja 2 del PDF descargable (BoletoHtml.kt).
                val secciones = terminosYCondicionesSecciones()

                secciones.forEach { (titulo, contenido) ->
                    Text(titulo, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.padding(top = 12.dp, bottom = 4.dp))
                    Text(contenido, style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                }

                Spacer(modifier = Modifier.height(12.dp))
                Text("Última actualización: Junio 2026", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
            }
        },
        confirmButton = {
            Button(onClick = onDismiss, colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro)) {
                Text("Entendido")
            }
        }
    )
}

@Composable
fun AvisoDePrivacidadDialog(onDismiss: () -> Unit) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Filled.PrivacyTip, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(24.dp))
                Spacer(modifier = Modifier.width(8.dp))
                Text("Aviso de Privacidad", fontWeight = FontWeight.Black, color = SmartVanColors.AzulOscuro)
            }
        },
        text = {
            Column(modifier = Modifier.verticalScroll(rememberScrollState())) {
                val secciones = listOf(
                    "Responsable" to
                        "SMARTVAN S.A.S. de C.V., con RFC SMA230328D67 y domicilio en Corregidora, Querétaro, es responsable del tratamiento de sus datos personales conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y su Reglamento.",

                    "Datos Personales Recabados" to
                        "Para la prestación del servicio, recabamos los siguientes datos personales:\n• Nombre completo\n• Número de teléfono\n• Dirección de correo electrónico\n• Punto de abordaje/descenso seleccionado\n\nSi solicita factura, recabamos además datos fiscales: RFC, razón social, régimen fiscal, uso de CFDI y código postal fiscal, con la única finalidad de emitir su comprobante.\n\nNo recabamos datos financieros de tarjeta directamente; los pagos con tarjeta/SPEI/OXXO son procesados por plataformas certificadas (Stripe). En pagos por transferencia, usted envía su comprobante a nuestro número de atención.",

                    "Finalidad del Tratamiento" to
                        "Sus datos personales serán utilizados para:\n• Generar y enviar sus boletos digitales\n• Elaborar el manifiesto de pasajeros del viaje\n• Comunicarnos con usted respecto a su reserva\n• Enviar confirmaciones y actualizaciones de su viaje\n• Emitir facturas (CFDI) cuando usted lo solicite\n• Cumplir con obligaciones legales y regulatorias del servicio de transporte",

                    "Transferencia de Datos" to
                        "Sus datos podrán ser compartidos con:\n• Operadores de transporte asociados (para la prestación del servicio)\n• Proveedores de servicios de correo electrónico (para envío de boletos)\n• Contador o proveedor de facturación (solo datos fiscales, para emitir su CFDI)\n• Autoridades competentes cuando sea requerido por ley\n\nNo vendemos, intercambiamos ni transferimos sus datos personales a terceros con fines comerciales o publicitarios.",

                    "Medidas de Seguridad" to
                        "Implementamos medidas técnicas y organizativas para proteger sus datos personales contra acceso no autorizado, pérdida o alteración. La información se almacena en servidores seguros con cifrado y controles de acceso.",

                    "Derechos ARCO" to
                        "Usted tiene derecho a Acceder, Rectificar, Cancelar u Oponerse al tratamiento de sus datos personales (Derechos ARCO), así como a revocar su consentimiento. Para ejercer estos derechos, envíe su solicitud al correo privacidad@smartvanmx.com o al WhatsApp de atención disponible en la plataforma, indicando su nombre completo y el derecho que desea ejercer. Daremos respuesta en un plazo máximo de 20 días hábiles conforme a la LFPDPPP.",

                    "Uso de Cookies y Tecnologías" to
                        "La plataforma puede utilizar tecnologías de almacenamiento local para mejorar la experiencia del usuario. Estos datos no identifican personalmente al usuario y se utilizan únicamente con fines funcionales.",

                    "Modificaciones al Aviso" to
                        "SmartVan se reserva el derecho de modificar el presente Aviso de Privacidad. Cualquier cambio será publicado en la plataforma y entrará en vigor inmediatamente después de su publicación.",

                    "Última actualización" to
                        "Junio de 2026."
                )

                secciones.forEach { (titulo, contenido) ->
                    Text(titulo, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.padding(top = 12.dp, bottom = 4.dp))
                    Text(contenido, style = MaterialTheme.typography.bodySmall, color = Color.DarkGray)
                }

                Spacer(modifier = Modifier.height(12.dp))
                Text("Última actualización: Junio 2026", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
            }
        },
        confirmButton = {
            Button(onClick = onDismiss, colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.AzulOscuro)) {
                Text("Entendido")
            }
        }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SelectorUbicacion(label: String, value: String, options: List<String>, onValueChange: (String) -> Unit) {
    var expanded by remember { mutableStateOf(false) }

    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = { expanded = it },
        modifier = Modifier.fillMaxWidth()
    ) {
        OutlinedTextField(
            value = value.ifEmpty { "Selecciona una ciudad..." },
            onValueChange = {},
            readOnly = true,
            label = { Text(label) },
            modifier = Modifier.fillMaxWidth().menuAnchor(),
            leadingIcon = {
                Icon(
                    imageVector = Icons.Filled.LocationOn,
                    contentDescription = "Ubicación",
                    tint = SmartVanColors.AzulOscuro
                )
            },
            trailingIcon = {
                ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded)
            },
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = SmartVanColors.AzulOscuro,
                unfocusedBorderColor = if(value.isEmpty()) Color.LightGray else SmartVanColors.AzulOscuro
            )
        )

        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false }
        ) {
            options.forEach { option ->
                DropdownMenuItem(
                    text = { Text(option, fontWeight = FontWeight.Medium) },
                    onClick = {
                        onValueChange(option)
                        expanded = false
                    },
                    contentPadding = ExposedDropdownMenuDefaults.ItemContentPadding
                )
            }
        }
    }
}
